import { routes } from '../../../../../utils/helpers/ApiClient';
import PhdDissertationExamApplicationsTable from '../PhdDissertationExamApplications/PhdDissertationExamApplicationsTable.jsx';
import PhdDissertationDefenseApplicationsTable from '../PhdDissertationDefenseApplications/PhdDissertationDefenseApplicationsTable.jsx';
import PhdSubjectsExamApplicationsTable from '../PhdSubjectsExamApplications/PhdSubjectsExamApplicationsTable.jsx';
import {
  getPhdDocumentDependencyState,
  PHD_DOCUMENT_DEPENDENCY_TYPES,
} from '../utils/phdDocumentDependencies.jsx';

const toComparableValue = (value) => String(value ?? '').trim();

const getStudentOwnedApplications = (applications, studentId) => {
  if (!Array.isArray(applications)) {
    return [];
  }

  const comparableStudentId = toComparableValue(studentId);
  if (!comparableStudentId) {
    return applications;
  }

  return applications.filter((application) => {
    const ownerId =
      application?.phd_student_id ??
      application?.student?.id ??
      application?.student_id;
    return toComparableValue(ownerId) === comparableStudentId;
  });
};

/**
 * Builds the three application-tab descriptors (dissertation exam, dissertation
 * defense, subjects exam) consumed by the PhD applications page. Pure data —
 * every context value is passed in so the component can memoize the result.
 *
 * @param {object} ctx Component state and RTK Query handles for each tab.
 * @returns {Array<object>} Tab descriptors.
 */
export const buildPhdApplicationTabs = ({
  t,
  dependencyStudent,
  isStudentContext,
  studentProfileId,
  canViewDissertationExam,
  canViewDissertationDefense,
  canViewSubjectsExam,
  dissertationExamApplications,
  dissertationDefenseApplications,
  subjectsExamApplications,
  dissertationExamPagination,
  dissertationDefensePagination,
  subjectsExamPagination,
  isLoadingDissertationExam,
  isLoadingDissertationDefense,
  isLoadingSubjectsExam,
  refetchDissertationExam,
  refetchDissertationDefense,
  refetchSubjectsExam,
  deleteDissertationExamApplication,
  deleteDissertationDefenseApplication,
  deleteSubjectsExamApplication,
}) => {
  const getCreateDependencyState = (documentType) =>
    getPhdDocumentDependencyState({
      documentType,
      student: dependencyStudent,
    });

  const getExistingStudentApplication = (applications) => {
    if (!isStudentContext) {
      return null;
    }

    const ownedApplications = getStudentOwnedApplications(
      applications,
      studentProfileId,
    );
    return ownedApplications[0] ?? null;
  };

  return [
    {
      value: 'dissertationExam',
      label: t('Prihlášky na dizertačnú skúšku'),
      studentLabel: t('Prihláška na dizertačnú skúšku'),
      canView: canViewDissertationExam,
      data: dissertationExamApplications ?? [],
      pagination: dissertationExamPagination,
      isLoading: isLoadingDissertationExam,
      refetch: refetchDissertationExam,
      deleteMutation: deleteDissertationExamApplication,
      existingStudentApplication: getExistingStudentApplication(
        dissertationExamApplications,
      ),
      createText: t('Pridať prihlášku na dizertačnú skúšku'),
      createUrl: `${routes.cmsPhdApplicationsCreate}?type=dissertationExam`,
      editUrl: routes.cmsPhdDissertationExamApplicationsEdit,
      TableComponent: PhdDissertationExamApplicationsTable,
      dataPropName: 'dissertationExamApplications',
      permissionsBasic: 'phd-dissertation-exam-applications',
      createDependencyState: getCreateDependencyState(
        PHD_DOCUMENT_DEPENDENCY_TYPES.DISSERTATION_EXAM_APPLICATION,
      ),
    },
    {
      value: 'dissertationDefense',
      label: t('Prihlášky na obhajobu dizertačnej práce'),
      studentLabel: t('Prihláška na obhajobu dizertačnej práce'),
      canView: canViewDissertationDefense,
      data: dissertationDefenseApplications ?? [],
      pagination: dissertationDefensePagination,
      isLoading: isLoadingDissertationDefense,
      refetch: refetchDissertationDefense,
      deleteMutation: deleteDissertationDefenseApplication,
      existingStudentApplication: getExistingStudentApplication(
        dissertationDefenseApplications,
      ),
      createText: t('Pridať prihlášku na obhajobu dizertačnej práce'),
      createUrl: `${routes.cmsPhdApplicationsCreate}?type=dissertationDefense`,
      editUrl: routes.cmsPhdDissertationDefenseApplicationsEdit,
      TableComponent: PhdDissertationDefenseApplicationsTable,
      dataPropName: 'applications',
      permissionsBasic: 'phd-dissertation-defense-applications',
      createDependencyState: getCreateDependencyState(
        PHD_DOCUMENT_DEPENDENCY_TYPES.DISSERTATION_DEFENSE_APPLICATION,
      ),
    },
    {
      value: 'subjectsExam',
      label: t('Prihlášky na štátnu skúšku z povinných predmetov'),
      studentLabel: t('Prihláška na štátnu skúšku z povinných predmetov'),
      canView: canViewSubjectsExam,
      data: subjectsExamApplications ?? [],
      pagination: subjectsExamPagination,
      isLoading: isLoadingSubjectsExam,
      refetch: refetchSubjectsExam,
      deleteMutation: deleteSubjectsExamApplication,
      existingStudentApplication: getExistingStudentApplication(
        subjectsExamApplications,
      ),
      createText: t('Pridať prihlášku na štátnu skúšku z povinných predmetov'),
      createUrl: `${routes.cmsPhdApplicationsCreate}?type=subjectsExam`,
      editUrl: routes.cmsPhdSubjectsExamApplicationsEdit,
      TableComponent: PhdSubjectsExamApplicationsTable,
      dataPropName: 'subjectsExamApplications',
      permissionsBasic: 'phd-subjects-exam-applications',
      createDependencyState: getCreateDependencyState(
        PHD_DOCUMENT_DEPENDENCY_TYPES.SUBJECTS_EXAM_APPLICATION,
      ),
    },
  ];
};
